import { createReadStream } from "fs";
import * as path from "path";
import { Agent } from "https";

import { Response } from "express";
import mime from "mime-types";
import fetch from "node-fetch";

import { IResultHandler } from "./IResultHandler";

export class StreamResultHandler implements IResultHandler {
    private readonly mimeType: undefined | string;

    public constructor(mimeType?: string) {
        this.mimeType = mimeType;
    }

    public async processResult(result: StreamReturnResult, _req: Express.Request, res: Response): Promise<void> {
        if (result instanceof StreamResult) {
            const mimeType = result.mimeType ?? this.mimeType;
            if (mimeType) {
                res.header("Content-Type", mimeType);
            }
            result.stream
                .on("error", error => {
                    if (error instanceof Error) {
                        res.status(404);
                        res.header("Content-Type", "text/plain");
                        res.send(`File not found`);
                        return;
                    }
                    throw error;
                })
                .pipe(res);
        } else if (result instanceof FileStreamResult) {
            const mimeType = result.mimeType ?? this.mimeType ?? mime.lookup(path.extname(result.filePath));
            if (mimeType) {
                res.header("Content-Type", mimeType);
            }
            createReadStream(result.filePath)
                .on("error", error => {
                    if (error instanceof Error) {
                        res.status(404);
                        res.header("Content-Type", "text/plain");
                        res.send(`${result.filePath} not found`);
                        return;
                    }
                    // eslint-disable-next-line @typescript-eslint/only-throw-error
                    throw error;
                })
                .pipe(res);
        } else if (result instanceof ProxyGetRequestToResult) {
            const mimeType = (await result.getMimeType()) ?? this.mimeType;
            if (mimeType) {
                res.header("Content-Type", mimeType);
            }
            (await result.getStream()).pipe(res);
        } else {
            if (this.mimeType) {
                res.header("Content-Type", this.mimeType);
            }
            result.pipe(res);
        }
    }
}

export class StreamResult {
    public readonly stream: NodeJS.ReadableStream;
    public readonly mimeType?: string;

    public constructor(stream: NodeJS.ReadableStream, mimeType?: string | false) {
        this.stream = stream;
        this.mimeType = mimeType === false ? undefined : mimeType;
    }
}

export class FileStreamResult {
    public readonly filePath: string;
    public readonly mimeType?: string;

    public constructor(filePath: string, mimeType?: string | false) {
        this.filePath = filePath;
        this.mimeType = mimeType === false ? undefined : mimeType;
    }
}

export class ProxyGetRequestToResult {
    public readonly url: string;
    public readonly mimeType?: string;

    public constructor(url: string, mimeType?: string) {
        this.url = url;
        this.mimeType = mimeType;
    }

    public async getStream(): Promise<NodeJS.ReadableStream> {
        if (this.url.startsWith("https")) {
            const response = await fetch(this.url, {
                method: "GET",
                agent: new Agent({ rejectUnauthorized: false }),
            });
            return response.body;
        } else {
            const response = await fetch(this.url, {
                method: "GET",
            });
            return response.body;
        }
    }

    public getMimeType(): Promise<string | undefined> | string | undefined {
        return this.mimeType;
    }
}

export type StreamReturnResult = StreamResult | FileStreamResult | NodeJS.ReadableStream | ProxyGetRequestToResult;
